import { Board } from './board';
import { Player } from './player';
import { CellState, GameState, Position, BoardConfig } from '../types';
import {
  checkWin,
  isValidMove,
  countConsecutiveInDirection,
} from '../utils/validator';
import { DIRECTIONS } from '../utils/patterns';

/**
 * Main game engine that orchestrates the game flow
 */
export class GameEngine {
  private board: Board;
  private players: [Player, Player];
  private currentPlayerIndex: number;
  private gameState: GameState;
  private winningLine: Position[] | null;
  private config: BoardConfig;
  private moveHistory: Position[];

  constructor(
    player1Name: string = 'Player 1',
    player2Name: string = 'Player 2',
    boardSize: number = 15,
    requireOpenEnd: boolean = true
  ) {
    this.board = new Board(boardSize);
    this.players = [
      new Player(player1Name, CellState.PLAYER_X),
      new Player(player2Name, CellState.PLAYER_O),
    ];
    this.currentPlayerIndex = 0;
    this.gameState = GameState.PLAYING;
    this.config = {
      size: boardSize,
      winLength: 5,
      requireOpenEnd,
    };
    this.moveHistory = [];
    this.winningLine = null;
  }

  /**
   * Gets the current player
   */
  getCurrentPlayer(): Player {
    return this.players[this.currentPlayerIndex];
  }

  /**
   * Gets the game state
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * Gets the board
   */
  getBoard(): Board {
    return this.board;
  }

  /**
   * Gets both players
   */
  getPlayers(): [Player, Player] {
    return this.players;
  }

  /**
   * Gets the move history
   */
  getMoveHistory(): Position[] {
    return [...this.moveHistory];
  }

  /**
   * Makes a move at the specified position
   */
  makeMove(row: number, col: number): boolean {
    // Check if game is still playing
    if (this.gameState !== GameState.PLAYING) {
      console.log('Game is already over!');
      return false;
    }

    // Validate the move
    if (!isValidMove(this.board, row, col)) {
      console.log('Invalid move! Cell is either occupied or out of bounds.');
      return false;
    }

    // Place the move
    const currentPlayer = this.getCurrentPlayer();
    const success = this.board.placeMove(row, col, currentPlayer.mark);

    if (!success) {
      return false;
    }

    // Add to move history
    this.moveHistory.push({ row, col });

    // Check for win and record winning line (positions)
    let foundWin = false;
    for (const direction of DIRECTIONS) {
      const lineResult = countConsecutiveInDirection(
        this.board,
        { row, col },
        direction,
        currentPlayer.mark
      );
      if (lineResult.count >= this.config.winLength) {
        if (this.config.requireOpenEnd) {
          if (lineResult.hasOpenStart || lineResult.hasOpenEnd) {
            this.winningLine = lineResult.positions;
            foundWin = true;
            break;
          }
        } else {
          this.winningLine = lineResult.positions;
          foundWin = true;
          break;
        }
      }
    }

    if (foundWin) {
      this.gameState =
        currentPlayer.mark === CellState.PLAYER_X
          ? GameState.PLAYER_X_WON
          : GameState.PLAYER_O_WON;
      return true;
    }

    // Check for draw
    if (this.board.isFull()) {
      this.gameState = GameState.DRAW;
      return true;
    }

    // Switch player
    this.switchPlayer();
    return true;
  }

  /**
   * Switches to the next player
   */
  private switchPlayer(): void {
    this.currentPlayerIndex = (this.currentPlayerIndex + 1) % 2;
  }

  /**
   * Resets the game
   */
  reset(): void {
    this.board.reset();
    this.currentPlayerIndex = 0;
    this.gameState = GameState.PLAYING;
    this.moveHistory = [];
    this.winningLine = null;
  }

  /**
   * Checks if the game is over
   */
  isGameOver(): boolean {
    return this.gameState !== GameState.PLAYING;
  }

  /**
   * Get the winning line positions (if any)
   */
  getWinningLine(): Position[] | null {
    return this.winningLine ? [...this.winningLine] : null;
  }

  /**
   * Gets the winner
   */
  getWinner(): Player | null {
    if (this.gameState === GameState.PLAYER_X_WON) {
      return this.players[0];
    } else if (this.gameState === GameState.PLAYER_O_WON) {
      return this.players[1];
    }
    return null;
  }

  /**
   * Displays the current game status
   */
  displayStatus(): void {
    this.board.display();

    if (this.gameState === GameState.PLAYING) {
      console.log(`Current player: ${this.getCurrentPlayer()}`);
    } else if (this.gameState === GameState.DRAW) {
      console.log('Game ended in a DRAW!');
    } else {
      const winner = this.getWinner();
      console.log(`🎉 ${winner} WINS! 🎉`);
    }

    console.log(`Moves played: ${this.moveHistory.length}`);
    if (this.config.requireOpenEnd) {
      console.log('Vietnamese Caro rules: Win requires open end');
    }
  }
}
